import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";

import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import ContactsOutlinedIcon from "@material-ui/icons/ContactsOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import AllInclusiveOutlinedIcon from "@material-ui/icons/AllInclusiveOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import SmsOutlinedIcon from "@material-ui/icons/SmsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import {
  DeviceHubOutlined,
} from "@material-ui/icons";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";
import rules from "../rules";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import { i18n } from "../translate/i18n";

import useCompanySettings from "../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  listItemActive: {
    backgroundColor: `${theme.palette.primary.main}1F`, // 12% alpha
    borderRadius: 10,
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: -12,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: 3,
      background: theme.palette.aerosGradient,
    },
    // O mockup usa o tom claro do primário no ícone ativo (#C08BFF), não o
    // primário cheio — sobre o fundo a 12% o cheio fica escuro demais.
    "& .MuiListItemIcon-root": { color: theme.palette.primary.light },
    "& .MuiListItemText-primary": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
  listItem: {
    // Espaçamento do mockup: item 9x12 com 12px entre ícone e rótulo.
    borderRadius: 10,
    margin: "0 8px 2px",
    width: "auto",
    padding: "9px 12px",
    "& .MuiListItemIcon-root": {
      color: theme.palette.text.secondary,
      minWidth: 0,
      marginRight: 12,
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    "& .MuiListItemText-root": { margin: 0 },
    "& .MuiListItemText-primary": {
      fontSize: 13.5,
      color: theme.palette.text.secondary,
      fontWeight: 500,
      lineHeight: 1.4,
    },
    "&:hover": {
      backgroundColor: "transparent",
      "& .MuiListItemText-primary": { color: theme.palette.text.primary },
      "& .MuiListItemIcon-root": { color: theme.palette.text.primary },
    },
  },
  groupHeader: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.palette.aeros.textTertiary,
    lineHeight: 1.5,
  },

  groupHeaderButton: {
    minHeight: 44,
    cursor: "pointer",
    borderRadius: 10,
    margin: "1px 8px",
    width: "auto",
    paddingTop: 18,
    paddingBottom: 4,
    "&:hover": { backgroundColor: theme.palette.aeros.surface2 },
    "& .MuiListItemText-root": { margin: 0 },
  },

  groupHeaderChevron: {
    fontSize: 16,
    color: theme.palette.aeros.textTertiary,
    transition: "transform 200ms ease",
  },

  groupHeaderChevronOpen: {
    transform: "rotate(180deg)",
  },

  groupHeaderBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 6,
    minWidth: 14,
    height: 14,
    padding: "0 3px",
    borderRadius: 7,
    fontSize: 9,
    fontWeight: 700,
    backgroundColor: "rgba(248,113,113,0.15)",
    color: "#F87171",
    verticalAlign: "middle",
  },

  // Badge melhorado mas mantendo funcionalidade
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ef4444",
      color: "#fff",
      fontSize: "0.75rem",
      fontWeight: 600,
      animation: "$pulse 2s infinite",
    }
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    }
  },

  // Melhorias para submenus mantendo estrutura original
  submenuContainer: {
    backgroundColor: theme.mode === "light"
      ? "rgba(0, 0, 0, 0.02)"
      : "rgba(255, 255, 255, 0.02)",
  },

  // Tooltip melhorado
  customTooltip: {
    backgroundColor: theme.mode === "light" ? "#1e293b" : "#374151",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    "& .MuiTooltip-arrow": {
      color: theme.mode === "light" ? "#1e293b" : "#374151",
    }
  },

  // Efeitos suaves para expand/collapse
  expandIcon: {
    transition: "transform 0.3s ease",
    color: theme.palette.primary.main, // Usa cor do tema
    "&.expanded": {
      transform: "rotate(180deg)",
    }
  },

  // Menu container com melhorias sutis
  menuContainer: {
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "3px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0, 0, 0, 0.2)"
          : "rgba(255, 255, 255, 0.2)",
      }
    },
  },
}));

// Espelha o check() de components/Can/index.js em forma booleana, para
// combinar (OR) a visibilidade de vários itens de um mesmo grupo antes de
// decidir se o cabeçalho (GroupHeader) do grupo deve aparecer.
const hasPermission = (role, action) => {
  const permissions = rules[role];
  if (!permissions) return false;
  const staticPermissions = permissions.static;
  if (staticPermissions && staticPermissions.includes(action)) return true;
  const dynamicPermissions = permissions.dynamic;
  if (dynamicPermissions && dynamicPermissions[action]) return true;
  return false;
};

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, active } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = active || activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={`${classes.listItem} ${isActive ? classes.listItemActive : ""}`}
        >
          {icon ? (
            <ListItemIcon>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  {icon}
                </Badge>
              ) : (
                icon
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText primary={primary} />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

// Chaves usadas em localStorage("menuGroupsState") e nas rotas de cada
// grupo, para decidir qual grupo deve abrir automaticamente quando a rota
// ativa pertence a ele (nav-state-active: o item ativo nunca pode ficar
// escondido dentro de um grupo fechado).
const MENU_GROUP_ROUTE_PREFIXES = {
  // Cinco grupos do mockup "Tela Aeros Dark - Atendimentos Nova Entrada"
  // (sidebar reorganizada em 10/2026) + Plataforma, só do super admin.
  visao: ["/", "/moments", "/reports"],
  atendimento: [
    "/tickets",
    "/kanban",
    "/schedules",
    "/quick-messages",
    "/chats",
    "/call-historicals",
  ],
  crm: ["/contacts", "/leads", "/tags"],
  automacao: [
    "/campaigns",
    "/contact-lists",
    "/campaigns-config",
    "/files",
    "/flowbuilders",
    "/flowbuilder",
    "/phrase-lists",
    "/flow-default",
    "/prompts",
    "/birthday-settings",
    "/queues",
  ],
  sistema: [
    "/connections",
    "/allConnections",
    "/queue-integration",
    "/messages-api",
    "/users",
    "/wallets",
    "/financeiro",
    "/creditos-sms",
    "/settings",
    "/helps",
    "/announcements",
  ],
  plataforma: ["/companies", "/plans"],
};

const getActiveMenuGroup = (pathname) => {
  if (pathname === "/") return "visao";
  const groups = Object.keys(MENU_GROUP_ROUTE_PREFIXES);
  for (const group of groups) {
    const matches = MENU_GROUP_ROUTE_PREFIXES[group].some(
      (prefix) => prefix !== "/" && pathname.startsWith(prefix)
    );
    if (matches) return group;
  }
  return null;
};

function GroupHeader({ label, open, onToggle, showBadge, classes }) {
  return (
    <ListItem
      button
      disableGutters
      onClick={onToggle}
      aria-expanded={open}
      className={classes.groupHeaderButton}
    >
      <ListItemText
        primary={
          <span className={classes.groupHeader}>
            {label}
            {showBadge && (
              <span className={classes.groupHeaderBadge}>!</span>
            )}
          </span>
        }
      />
      <ExpandMoreIcon
        aria-hidden="true"
        className={`${classes.groupHeaderChevron} ${open ? classes.groupHeaderChevronOpen : ""
          }`}
      />
    </ListItem>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);


  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);

  const [groupsOpen, setGroupsOpen] = useState({
    visao: false,
    atendimento: false,
    crm: false,
    automacao: false,
    sistema: false,
    plataforma: false,
  });

  const toggleMenuGroup = (group) => {
    setGroupsOpen((prev) => {
      const next = { ...prev, [group]: !prev[group] };
      try {
        localStorage.setItem("menuGroupsState", JSON.stringify(next));
      } catch (err) {
        // localStorage indisponível (modo privado, quota, etc): ignora persistência
      }
      return next;
    });
  };

  // Lê o estado salvo uma única vez no mount (não reagimos a mudanças
  // externas de localStorage depois disso — é só a leitura inicial da
  // preferência do usuário).
  useEffect(() => {
    let stored = null;
    try {
      const raw = localStorage.getItem("menuGroupsState");
      if (raw) stored = JSON.parse(raw);
    } catch (err) {
      stored = null;
    }

    if (stored && typeof stored === "object" && !Array.isArray(stored)) {
      setGroupsOpen((prev) => ({ ...prev, ...stored }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Força abertura do grupo da rota ativa a cada mudança de rota (não só no
  // mount): MainListItems fica montado entre navegações da SPA, então um
  // link fora da sidebar (ex.: um card do dashboard) pode trocar a rota
  // ativa para outro grupo sem passar por um clique no cabeçalho. Regras:
  // - Merge aditivo: nunca fecha outro grupo que já esteja aberto.
  // - Não persiste em localStorage — é abertura forçada por navegação, não
  //   escolha do usuário; o toggle manual (toggleMenuGroup) continua sendo
  //   o único caminho que grava em "menuGroupsState".
  // - Se o usuário fechar manualmente o grupo da rota ativa, ele permanece
  //   fechado até a próxima mudança de rota (ou re-render deste efeito),
  //   quando volta a forçar abertura — aceito e documentado aqui.
  useEffect(() => {
    const activeGroup = getActiveMenuGroup(location.pathname);
    if (!activeGroup) return;
    setGroupsOpen((prev) =>
      prev[activeGroup] ? prev : { ...prev, [activeGroup]: true }
    );
  }, [location.pathname]);

  const isFlowbuilderRouteActive = ["/phrase-lists", "/flowbuilder", "/flow-default"].some(
    (p) => location.pathname.startsWith(p)
  );

  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        setShowWallets(setting.DirectTicketsToWallets);

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);


  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      console.log('Registrando listener para:', eventName);

      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          console.log('Removendo listener para:', eventName);
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  // Papéis "elevados" usados pelos itens: um `user` comum pode ganhar o menu
  // de admin em pontos específicos via flags do cadastro (showDashboard,
  // allowRealTime, allowConnections).
  const dashboardRole =
    user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile;
  const realTimeRole =
    user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile;
  const connectionsRole =
    user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile;

  const canSeeOverview =
    hasPermission(dashboardRole, "drawer-admin-items:view") ||
    hasPermission(realTimeRole, "drawer-admin-items:view");
  const canSeeAutomation =
    (user?.showCampaign === "enabled" && showCampaigns) ||
    user.showFlow === "enabled" ||
    hasPermission(connectionsRole, "dashboard:view");
  const canSeeSystem =
    hasPermission(connectionsRole, "dashboard:view") || hasHelps || !!user.super;

  return (
    <div onClick={drawerClose}>
      {/* ───────── Visão Geral ───────── */}
      {!collapsed && canSeeOverview && (
        <GroupHeader
          label={i18n.t("mainDrawer.listItems.groupOverview")}
          open={groupsOpen.visao}
          onToggle={() => toggleMenuGroup("visao")}
          classes={classes}
        />
      )}
      <Collapse in={collapsed || groupsOpen.visao} timeout="auto" unmountOnExit={false}>
        <Can
          role={dashboardRole}
          perform={"drawer-admin-items:view"}
          yes={() => (
            <ListItemLink
              to="/"
              primary={i18n.t("mainDrawer.listItems.dashboard")}
              icon={<SpaceDashboardOutlinedIcon />}
              tooltip={collapsed}
            />
          )}
        />
        <Can
          role={realTimeRole}
          perform={"drawer-admin-items:view"}
          yes={() => (
            <ListItemLink
              to="/moments"
              primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
              icon={<MonitorHeartOutlinedIcon />}
              tooltip={collapsed}
            />
          )}
        />
        <Can
          role={dashboardRole}
          perform={"drawer-admin-items:view"}
          yes={() => (
            <ListItemLink
              to="/reports"
              primary={i18n.t("mainDrawer.listItems.reports")}
              icon={<BarChartOutlinedIcon />}
              tooltip={collapsed}
            />
          )}
        />
      </Collapse>

      {/* ───────── Atendimento ───────── */}
      {!collapsed && (
        <GroupHeader
          label={i18n.t("mainDrawer.listItems.groupService")}
          open={groupsOpen.atendimento}
          onToggle={() => toggleMenuGroup("atendimento")}
          classes={classes}
        />
      )}
      <Collapse in={collapsed || groupsOpen.atendimento} timeout="auto" unmountOnExit={false}>
        <ListItemLink
          to="/tickets"
          primary={i18n.t("mainDrawer.listItems.tickets")}
          icon={<ChatOutlinedIcon />}
          tooltip={collapsed}
        />
        {showKanban && (
          <ListItemLink
            to="/kanban"
            primary={i18n.t("mainDrawer.listItems.kanban")}
            icon={<ViewKanbanOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
        {showSchedules && (
          <ListItemLink
            to="/schedules"
            primary={i18n.t("mainDrawer.listItems.schedules")}
            icon={<ScheduleOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
        <ListItemLink
          to="/quick-messages"
          primary={i18n.t("mainDrawer.listItems.quickMessages")}
          icon={<BoltOutlinedIcon />}
          tooltip={collapsed}
        />
        {showInternalChat && (
          <ListItemLink
            to="/chats"
            primary={i18n.t("mainDrawer.listItems.chats")}
            icon={
              <Badge color="secondary" variant="dot" invisible={invisible}>
                <ForumOutlinedIcon />
              </Badge>
            }
            tooltip={collapsed}
          />
        )}
        {showWavoipCall && (
          <ListItemLink
            to="/call-historicals"
            primary={i18n.t("mainDrawer.listItems.callHistorical")}
            icon={<HistoryToggleOffOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
        {/*
        <ListItemLink
          to="/todolist"
          primary={i18n.t("ToDoList")}
          icon={<EventAvailableOutlinedIcon />}
        />
        */}
      </Collapse>

      {/* ───────── CRM & Prospecção ───────── */}
      {!collapsed && (
        <GroupHeader
          label={i18n.t("mainDrawer.listItems.groupCrm")}
          open={groupsOpen.crm}
          onToggle={() => toggleMenuGroup("crm")}
          classes={classes}
        />
      )}
      <Collapse in={collapsed || groupsOpen.crm} timeout="auto" unmountOnExit={false}>
        {user.showContacts === "enabled" && (
          <ListItemLink
            to="/contacts"
            primary={i18n.t("mainDrawer.listItems.contacts")}
            icon={<ContactsOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
        <ListItemLink
          to="/leads"
          primary={i18n.t("mainDrawer.listItems.leads")}
          icon={<StorageOutlinedIcon />}
          tooltip={collapsed}
        />
        <ListItemLink
          to="/tags"
          primary={i18n.t("mainDrawer.listItems.tags")}
          icon={<SellOutlinedIcon />}
          tooltip={collapsed}
        />
      </Collapse>

      {/* ───────── Automação & Disparos ───────── */}
      {!collapsed && canSeeAutomation && (
        <GroupHeader
          label={i18n.t("mainDrawer.listItems.groupAutomation")}
          open={groupsOpen.automacao}
          onToggle={() => toggleMenuGroup("automacao")}
          classes={classes}
        />
      )}
      <Collapse in={collapsed || groupsOpen.automacao} timeout="auto" unmountOnExit={false}>
        {user?.showCampaign === "enabled" && showCampaigns && (
          <ListItemLink
            to="/campaigns"
            primary={i18n.t("mainDrawer.listItems.campaigns")}
            icon={<CampaignOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
        {/* FLOWBUILDER — item único; Fluxos / Frases gatilho / Fluxo padrão
            são abas internas (components/FlowBuilderNav) */}
        {user.showFlow === "enabled" && (
          <ListItemLink
            to="/flowbuilders"
            primary={i18n.t("aeros.flow.title")}
            icon={<AccountTreeOutlinedIcon />}
            tooltip={collapsed}
            active={isFlowbuilderRouteActive}
          />
        )}
        <Can
          role={connectionsRole}
          perform="dashboard:view"
          yes={() => (
            <>
              {showOpenAi && (
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/prompts"
                      primary={i18n.t("mainDrawer.listItems.prompts")}
                      icon={<AllInclusiveOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
              )}
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/birthday-settings"
                    primary={i18n.t("mainDrawer.listItems.birthdaySettings")}
                    icon={<CakeOutlinedIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/queues"
                    primary={i18n.t("mainDrawer.listItems.queues")}
                    icon={<DeviceHubOutlined />}
                    tooltip={collapsed}
                  />
                )}
              />
            </>
          )}
        />
      </Collapse>

      {/* ───────── Sistema & Conta ───────── */}
      {!collapsed && canSeeSystem && (
        <GroupHeader
          label={i18n.t("mainDrawer.listItems.groupSystem")}
          open={groupsOpen.sistema}
          onToggle={() => toggleMenuGroup("sistema")}
          showBadge={!groupsOpen.sistema && connectionWarning}
          classes={classes}
        />
      )}
      <Collapse in={collapsed || groupsOpen.sistema} timeout="auto" unmountOnExit={false}>
        <Can
          role={connectionsRole}
          perform="dashboard:view"
          yes={() => (
            <>
              <Can
                role={connectionsRole}
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <ListItemLink
                    to="/connections"
                    primary={i18n.t("mainDrawer.listItems.connections")}
                    icon={<SyncAltOutlinedIcon />}
                    showBadge={connectionWarning}
                    tooltip={collapsed}
                  />
                )}
              />
              {showIntegrations && (
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/queue-integration"
                      primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                      icon={<HubOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
              )}
              {showExternalApi && (
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/messages-api"
                      primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                      icon={<CodeRoundedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
              )}
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/users"
                    primary={i18n.t("mainDrawer.listItems.users")}
                    icon={<GroupOutlinedIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
              {user.profile === "admin" && showWallets && (
                <ListItemLink
                  to="/wallets"
                  primary={i18n.t("mainDrawer.listItems.wallets")}
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  tooltip={collapsed}
                />
              )}
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/financeiro"
                    primary={i18n.t("mainDrawer.listItems.financeiro")}
                    icon={<PaymentsOutlinedIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/creditos-sms"
                    primary={i18n.t("mainDrawer.listItems.creditosSms")}
                    icon={<SmsOutlinedIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/settings"
                    primary={i18n.t("mainDrawer.listItems.settings")}
                    icon={<SettingsOutlinedIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
            </>
          )}
        />
        {hasHelps && (
          <ListItemLink
            to="/helps"
            primary={i18n.t("mainDrawer.listItems.helps")}
            icon={<HelpOutlineIcon />}
            tooltip={collapsed}
          />
        )}
        {user.super && (
          <ListItemLink
            to="/announcements"
            primary={i18n.t("mainDrawer.listItems.annoucements")}
            icon={<NotificationsOutlinedIcon />}
            tooltip={collapsed}
          />
        )}
      </Collapse>

      {/* ───────── Plataforma (super admin) ───────── */}
      {user.super && (
        <>
          {!collapsed && (
            <GroupHeader
              label={i18n.t("mainDrawer.listItems.groupPlatform")}
              open={groupsOpen.plataforma}
              onToggle={() => toggleMenuGroup("plataforma")}
              classes={classes}
            />
          )}
          <Collapse
            in={collapsed || groupsOpen.plataforma}
            timeout="auto"
            unmountOnExit={false}
          >
            <ListItemLink
              to="/companies"
              primary={i18n.t("mainDrawer.listItems.companies")}
              icon={<BusinessOutlinedIcon />}
              tooltip={collapsed}
            />
            <ListItemLink
              to="/plans"
              primary={i18n.t("mainDrawer.listItems.plans")}
              icon={<CardMembershipOutlinedIcon />}
              tooltip={collapsed}
            />
          </Collapse>
        </>
      )}
    </div>
  );
};

export default MainListItems;
